import { useEffect, useState } from "react";
import { ShieldCheck, Activity, Filter } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";

const ACTION_COLORS = {
  "user.login": "text-emerald-400",
  "user.register": "text-cyan-400",
  "billing.upgrade": "text-amber-400",
  "billing.cancel": "text-rose-400",
  "billing.auto_renew_changed": "text-slate-400",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => {
    api.get("/admin/audit", { params: filter ? { action: filter } : {} }).then((r) => setLogs(r.data));
  };

  useEffect(() => {
    api.get("/admin/audit/actions").then((r) => setActions(r.data));
  }, []);
  useEffect(load, [filter]);

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Admin · Compliance</div>
        <h1 className="font-heading text-4xl tracking-tight">Audit Log</h1>
        <p className="text-sm text-slate-400 mt-1">Immutable timeline of all platform actions</p>
      </header>

      <div className="ae-card p-4 flex items-center gap-3">
        <Filter className="h-4 w-4 text-slate-500" />
        <select data-testid="audit-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-1.5 text-sm">
          <option value="">All actions</option>
          {actions.map((a) => (
            <option key={a.action} value={a.action}>{`${a.action} (${a.count})`}</option>
          ))}
        </select>
        <span className="text-xs text-slate-500">{logs.length} events</span>
      </div>

      <div className="ae-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Target</th>
              <th className="text-left px-4 py-3">Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-slate-500">
                <ShieldCheck className="h-8 w-8 mx-auto text-slate-700 mb-2" /> No audit entries
              </td></tr>
            ) : logs.map((l) => (
              <tr key={l.aid} data-testid={`audit-row-${l.aid}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3 text-xs font-mono text-slate-400 whitespace-nowrap">
                  {fmtDateTime(l.created_at)}
                </td>
                <td className="px-4 py-3">
                  {l.actor ? (
                    <>
                      <div className="text-sm">{l.actor.name}</div>
                      <div className="text-[11px] text-slate-500">{l.actor.email}</div>
                    </>
                  ) : (
                    <span className="text-slate-500 text-xs">system</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-mono font-semibold ${ACTION_COLORS[l.action] || "text-slate-300"}`}>{l.action}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">
                  {l.target_type ? `${l.target_type}:${l.target_id?.slice(0, 8) || ""}` : "N/A"}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 max-w-md truncate">
                  {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-slate-600 text-center">
        Audit entries are append-only. They cannot be edited or deleted.
      </div>
    </div>
  );
}
